import type { RenapApplicant } from '@/features/renap/services/renap.services'
import { axiosPrivate } from '@/shared/api/axios'
import { buildListQueryString } from '@/shared/api/build-list-query-string'
import { API } from '@/shared/constants/api'
import type { IClientAdminRow, IClientsAdminParams, IPaginated } from '../models/client-admin'

export async function getClientsAdmin(
  params: IClientsAdminParams
): Promise<IPaginated<IClientAdminRow>> {
  const qs = buildListQueryString(params)
  const { data } = await axiosPrivate.get<IPaginated<IClientAdminRow>>(
    `${API.clients.root}${qs ? `?${qs}` : ''}`
  )
  return data
}

export interface QuickCreateClientPayload {
  clientType: 'individual' | 'juridical'
  firstName: string
  lastName: string
  /** Razón social (sólo persona jurídica): identifica al cliente-empresa. */
  companyName?: string
  identificationNumber?: string
  identificationType?: string
  clientEmail: string
  advisorId?: string
  /** Definición de flujo elegida (motor de flujos): dirige tipo/secciones/nivel/vigencia. */
  flowDefinitionId: string
  expirationDays?: number
}

/** Alta rápida de cliente (autogestión): crea cliente esqueleto + envía flujo. */
export async function quickCreateClient(payload: QuickCreateClientPayload) {
  const { data } = await axiosPrivate.post(API.portalForms.quickClients, payload)
  return data
}

export interface VerifyQuickClientPayload {
  clientType: 'individual' | 'juridical'
  identificationNumber: string
  identificationType?: string
}

/**
 * Verifica la identidad ANTES de crear: el backend valida que el DPI no sea ya un cliente
 * (si lo es, responde CLIENT_DUPLICATE) y sólo entonces consulta RENAP, devolviendo la
 * identidad (applicant + foto) para confirmarla. No crea nada.
 */
export async function verifyQuickClient(
  payload: VerifyQuickClientPayload
): Promise<{ applicant: RenapApplicant; photo?: string }> {
  const { data } = await axiosPrivate.post(API.portalForms.quickClientsVerify, payload)
  return data
}

/**
 * Soft-deletes a client and frees its DPI/NIT (admin only on the backend). The
 * client can then be recreated with the same identification.
 */
export async function deleteClient(id: string) {
  const { data } = await axiosPrivate.delete(API.clients.byId(id))
  return data
}

/**
 * Marca a un cliente como revalidado a mano (se gestionó fuera de la plataforma): reinicia el
 * reloj de revalidación → sale de "Vencido" y vuelve a "Al día". NO envía flujo ni correo.
 */
export async function markClientRevalidated(clientId: string) {
  const { data } = await axiosPrivate.patch(API.portalForms.markClientVerified(clientId))
  return data
}
